<script setup>
import { ref, reactive, onMounted, computed } from "vue";
import { Handle, Position, useVueFlow } from "@vue-flow/core";
import { removeNodeOccurrences } from "@/mixins/common-utils.js";
import { NodeToolbar } from "@vue-flow/node-toolbar";

const props = defineProps(["nodeInfo"]);
// Computed property to check if there are errors
const hasErrors = computed(() => props.nodeInfo.data?.errors?.length > 0);
// Computed property to check if there are warnings
const hasWarnings = computed(() => props.nodeInfo.data?.warnings?.length > 0);
const { nodes, removeNodes, setViewport, findNode } = useVueFlow();
const node = ref(findNode(props.nodeInfo.id));
const emit = defineEmits([
    "toggleDialogOperator",
    "preview",
    "toggleDialogDebug",
]);
const iterator = reactive({
    isPresent: props.nodeInfo.label == "Iterator",
    isSetted: false,
});
const linkNestedMashup = ref(null);

const computedDebugSize = computed(() => {
    let size = 50;
    // Increase size if both errors and warnings are present
    if (hasErrors.value && hasWarnings.value) {
        size += 30; // Adjust the value as needed
    }
    return size;
});

onMounted(() => {
    setIteratorOp();
});

// Updates URL based on 'innerProcess' data within a node from 'mashup' in sessionStorage and manages iterator state.
function setIteratorOp() {
    // Exit the function early if the iterator is not present
    if (!iterator.isPresent) return;
    // Retrieve the 'mashup' object from sessionStorage
    const mashup = getMashupFromSession();
    // Find the specific node by its ID
    const node = findNodeById(mashup.nodes, props.nodeInfo.id);
    // If the node has 'innerProcess' data, update the iterator state and link
    if (node?.userData?.innerProcess) {
        iterator.isSetted = true;
        linkNestedMashup.value = createUpdatedUrl(node.userData.innerProcess);
    } else {
        // If 'innerProcess' data is not found, set the iterator state to false
        iterator.isSetted = false;
    }
}

// Helper function to get the 'mashup' object from sessionStorage
function getMashupFromSession() {
    return JSON.parse(sessionStorage.getItem("mashup"));
}

// Helper function to find a node by its ID within an array of nodes
function findNodeById(nodes, id) {
    return nodes.find((node) => node.id === id);
}

// Helper function to create an updated URL based on 'innerProcess' data
function createUpdatedUrl(innerProcess) {
    let parsedUrl = new URL(window.location.href);
    let pathSegments = parsedUrl.pathname.split("/");
    // Update the project segment with the 'project' from 'innerProcess'
    pathSegments[3] = innerProcess.project;
    // Update the version segment with the 'id' from 'innerProcess'
    pathSegments[5] = innerProcess.id;
    // Reconstruct the pathname with the updated segments
    parsedUrl.pathname = pathSegments.join("/");
    return parsedUrl.href;
}

function onOpenInnerMashup() {
    window.open(linkNestedMashup.value, "_blank");
}

// remove a single node from the graph
function onRemoveNode(id) {
    // Remove node occurrence
    removeNodeOccurrences(id, nodes);
    //Removes the node
    removeNodes(id);
}

//Manages node position to allow user to use editor while setting it
function manageNodePosition() {
    const sheetWidth =
        (document.getElementById("viewport-flow").offsetWidth - 350) / 2;
    setViewport({
        x: sheetWidth - props.nodeInfo.position.x,
        y: -props.nodeInfo.position.y + 50,
        zoom: 1,
    });
}

function editNode() {
    emit("toggleDialogOperator", true, props.nodeInfo.id);
    manageNodePosition();
}

//Call mashup Preview
function onPreview() {
    emit("preview", true, props.nodeInfo.label, props.nodeInfo.id);
}
//Call mashup Debug
function onDebug() {
    emit(
        "toggleDialogDebug",
        true,
        props.nodeInfo.id,
        props.nodeInfo.label,
        props.nodeInfo.data,
    );
}
</script>

<template>
    <div>
        <NodeToolbar
            :is-visible="nodeInfo.data?.toolbarVisible"
            :position="nodeInfo.data?.toolbarPosition"
            align="start"
            offset="8"
            class="mx-1"
        >
            <v-tooltip :text="$t('labels.setting')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        v-bind="props"
                        density="compact"
                        icon="mdi-cog"
                        color="grey-darken-4"
                        class="ml-1"
                        size="small"
                        @click="editNode()"
                    ></v-btn
                ></template>
            </v-tooltip>
            <v-tooltip
                :text="$t('labels.nested-mashup')"
                location="top"
                v-if="iterator.isPresent && iterator.isSetted"
            >
                <template v-slot:activator="{ props }">
                    <v-btn
                        v-bind="props"
                        density="compact"
                        icon="mdi-cube-scan"
                        color="purple-lighten-1"
                        class="ml-1"
                        size="small"
                        @click="onOpenInnerMashup"
                    ></v-btn
                ></template>
            </v-tooltip>
            <v-tooltip :text="$t('labels.preview')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        v-bind="props"
                        density="compact"
                        icon="mdi-play"
                        color="green-lighten-1"
                        class="ml-1"
                        size="small"
                        @click="onPreview()"
                    ></v-btn>
                </template>
            </v-tooltip>
            <v-tooltip :text="$t('labels.help')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        v-bind="props"
                        density="compact"
                        icon="mdi-help"
                        color="blue-lighten-1"
                        class="ml-1"
                        size="small"
                    >
                    </v-btn
                ></template>
            </v-tooltip>
            <v-tooltip :text="$t('labels.delete')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        v-bind="props"
                        density="compact"
                        icon="mdi-delete"
                        color="red-lighten-1"
                        class="ml-1"
                        size="small"
                        @click="onRemoveNode(nodeInfo.id)"
                    ></v-btn
                ></template>
            </v-tooltip>
            <v-tooltip :text="$t('labels.cache')" location="top">
                <template v-slot:activator="{ props }">
                    <v-btn
                        v-bind="props"
                        density="compact"
                        icon="mdi-database"
                        color="blue-grey-lighten-1"
                        class="ml-1"
                        size="small"
                    ></v-btn
                ></template>
            </v-tooltip>
        </NodeToolbar>

        <v-tooltip
            :text="$t('strings.double_click_to_setup')"
            location="bottom"
        >
            <template v-slot:activator="{ props }">
                <v-card
                    v-bind="props"
                    class="mx-auto"
                    :color="nodeInfo.data.color"
                    :ripple="false"
                    variant="elevated"
                    @dblclick="editNode()"
                    @click="setIteratorOp()"
                >
                    <v-card-item class="py-1 px-2">
                        <template v-slot:prepend>
                            <v-avatar
                                size="small"
                                :color="`${nodeInfo.data.color}-lighten-3`"
                            >
                                <v-icon
                                    :icon="nodeInfo.data.icon"
                                    :color="`${nodeInfo.data.color}-darken-4`"
                                ></v-icon>
                            </v-avatar>
                        </template>
                        <template v-slot:title>
                            <div class="text-overline font-weight-medium">
                                {{
                                    node.userData && node.userData.label
                                        ? node.userData.label
                                        : nodeInfo.label
                                }}
                            </div>
                        </template>
                        <template v-slot:subtitle>
                            <div
                                class="text-caption font-weight-light text-capitalize text-center"
                            >
                                {{ nodeInfo.type }} - {{ nodeInfo.id }}
                            </div>
                        </template>
                        <template v-slot:append>
                            <v-sheet
                                v-if="hasErrors || hasWarnings"
                                :color="`${nodeInfo.data.color}-lighten-3`"
                                rounded="sm"
                                :width="computedDebugSize"
                                height="50"
                                class="d-inline-flex align-center"
                            >
                                <!-- Error Badge -->
                                <v-badge
                                    v-if="hasErrors"
                                    color="error"
                                    :content="nodeInfo.data?.errors?.length"
                                    :bordered="true"
                                    class="mx-auto"
                                    offset-y="-2"
                                    offset-x="1"
                                >
                                    <v-btn
                                        icon="mdi-alert-octagon"
                                        density="compact"
                                        color="error"
                                        class="no-text"
                                        @click="onDebug"
                                    >
                                        <v-icon
                                            icon="mdi-alert-octagon"
                                            size="small"
                                        ></v-icon>
                                        <v-tooltip
                                            activator="parent"
                                            location="top"
                                        >
                                            {{ $t("strings.operator-error") }}
                                        </v-tooltip>
                                    </v-btn>
                                </v-badge>
                                <!-- Warning Badge -->
                                <v-badge
                                    v-if="hasWarnings"
                                    color="warning"
                                    :content="nodeInfo.data?.warnings?.length"
                                    :bordered="true"
                                    class="mx-auto"
                                    offset-y="-2"
                                    offset-x="3"
                                >
                                    <v-btn
                                        icon="mdi-alert"
                                        density="compact"
                                        color="warning"
                                        class="no-text"
                                        @click="onDebug"
                                    >
                                        <v-icon
                                            icon="mdi-alert"
                                            size="small"
                                        ></v-icon>
                                        <v-tooltip
                                            activator="parent"
                                            location="top"
                                        >
                                            {{ $t("strings.operator-warning") }}
                                        </v-tooltip>
                                    </v-btn>
                                </v-badge>
                            </v-sheet>
                        </template>
                    </v-card-item>
                </v-card>
            </template>
        </v-tooltip>

        <Handle
            type="target"
            :position="Position.Left"
            class="handleStyle handleStyleTarget"
            :id="`left-handle-${nodeInfo.id}`"
        />

        <div
            v-if="
                (nodeInfo.label || '').toString().trim().toLowerCase() ===
                'if-else'
            "
        >
            <Handle
                type="source"
                :position="Position.Right"
                class="handleStyle handleStyleSourceIf"
                :id="`right-handle-${nodeInfo.id}-if`"
            />
            <Handle
                type="source"
                :position="Position.Right"
                class="handleStyle handleStyleSourceElse"
                :id="`right-handle-${nodeInfo.id}-else`"
            />
        </div>
        <Handle
            v-else
            type="source"
            :position="Position.Right"
            class="handleStyle handleStyleSource"
            :id="`right-handle-${nodeInfo.id}`"
        />
    </div>
</template>

<style scoped>
.handleStyle {
    background: #999;
    height: 12px;
    width: 10px;
    border-radius: 3px;
    border: 0.5px solid #777;
}

.handleStyleSourceIf {
    top: 20px;
    background: greenyellow;
}
.handleStyleSourceElse {
    top: 44px;
    background: orangered;
}
</style>
